
import { useMemo } from 'react';
import {
  addDays as addDaysInstant,
  startOfDay,
  startOfWeek,
  viewerDayKey,
  weekDayStarts,
} from '@client/shared/calendar/week-grid.ts';
import { dayHeaderLabel } from '@client/shared/lib/wall-clock-intake.ts';
import { viewerZoneLabel } from '@client/shared/lib/viewer-timezone.ts';

export type CalendarAxisRange = 'week' | '3day' | 'day';

export function axisDayCount(range: CalendarAxisRange): number {
  return range === 'week' ? 7 : range === '3day' ? 3 : 1;
}

export interface CalendarAxisColumn {
  readonly key: string;
  readonly dayStart: Date;
  readonly label: string;
  readonly isToday: boolean;
}

export interface UseCalendarAxisArgs {
  readonly anchor: Date;
  readonly range: CalendarAxisRange;
  readonly zone: string;
  readonly now?: Date;
}

export interface CalendarAxis {
  readonly dayStarts: Date[];
  readonly days: string[];
  readonly columnHeaders: CalendarAxisColumn[];
  readonly todayKey: string;
  readonly zoneLabel: string;
}

export function useCalendarAxis({ anchor, range, zone, now }: UseCalendarAxisArgs): CalendarAxis {
  const nowInstant = useMemo(() => now ?? new Date(), [now]);
  const todayKey = useMemo(() => viewerDayKey(nowInstant, zone), [nowInstant, zone]);

  const dayStarts = useMemo<Date[]>(() => {
    if (range === 'week') return weekDayStarts(startOfWeek(anchor, zone), zone);
    const first = startOfDay(anchor, zone);
    const out: Date[] = [first];
    for (let i = 1; i < axisDayCount(range); i++) out.push(addDaysInstant(first, zone, i));
    return out;
  }, [range, anchor, zone]);

  const days = useMemo(() => dayStarts.map((d) => viewerDayKey(d, zone)), [dayStarts, zone]);

  const columnHeaders = useMemo<CalendarAxisColumn[]>(
    () =>
      dayStarts.map((dayStart, i) => {
        const key = days[i]!;
        return { key, dayStart, label: dayHeaderLabel(key, zone), isToday: key === todayKey };
      }),
    [dayStarts, days, zone, todayKey],
  );

  const zoneLabel = useMemo(() => viewerZoneLabel(nowInstant), [nowInstant]);

  return { dayStarts, days, columnHeaders, todayKey, zoneLabel };
}
